import { Box, Collapse, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import type { MouseEvent } from 'react';
import type { NavigateFunction } from 'react-router';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { ProductDto, ProductKey } from '@/types/dtos/orders/response/product.dto';
import { eventTypeToIcon } from '@/components/events/helpers/event-type-to-icon';
import type { EventType } from '@/types/dtos/events/event-type.enum';
import FeaturesStatusIcon from '@/assets/svg/features-status-icon/index';
import EventProductsSection from '@/components/events/event-products-section';
import EventRowActions from '@/components/events/event-row-actions';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto';
import { getOriginEventId } from '@/helpers/event.helper';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { computeEventCatalogQuotaIconState } from '@/helpers/event-quota-status.helper';

function getDraftOrderLineItemCount(event: EventDto) {
  const draftOrder = event.orders?.find(
    (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
  );
  return draftOrder?.lineItems.length ?? 0;
}

export default function EventListTableRow({
  event,
  catalogProducts,
  isLargeScreen,
  showMonetizeCatalog,
  expandedRows,
  toggleRowExpansion,
  navigate,
  onCatalogButtonClick,
}: {
  event: EventDto;
  catalogProducts: ProductDto[] | null | undefined;
  isLargeScreen: boolean;
  showMonetizeCatalog: boolean;
  expandedRows: Set<string>;
  toggleRowExpansion: (eventId: string) => void;
  navigate: NavigateFunction;
  onCatalogButtonClick: (
    clickEvent: MouseEvent<HTMLButtonElement> | undefined,
    eventDto: EventDto,
    productKey?: ProductKey,
  ) => void;
}) {
  const originId = getOriginEventId(event);
  const { data: balances } = useQuotaBalances('Event', !isLargeScreen && showMonetizeCatalog ? originId : undefined);

  const draftLineItemCount = getDraftOrderLineItemCount(event);
  const isExpanded = expandedRows.has(event.id);
  const catalogQuotaIconState = computeEventCatalogQuotaIconState(balances, catalogProducts ?? undefined, event);

  let navigateToUrl = `/event/${event.id}`;
  if (!event.multiFlow && event?.flows) {
    const singleFlow = event.flows[0];
    if (singleFlow) {
      const eventId = event.origin ? (typeof event.origin === 'string' ? event.origin : event.origin.id) : event.id;
      navigateToUrl = `/flow/${singleFlow.id}?contextType=event&contextId=${eventId}`;
    }
  }

  return (
    <TableRow
      onClick={() => navigate(navigateToUrl)}
      sx={{
        'verticalAlign': 'top',
        'borderColor': 'divider',
        'cursor': 'pointer',
        'transition': 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '&:not(:last-of-type)': {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <TableCell sx={{ pt: { xs: 2, sm: 2.8 }, px: { xs: 0, sm: 1 }, width: '40px', color: 'action.active' }}>
        {eventTypeToIcon(event.type as EventType)}
      </TableCell>
      <TableCell sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 1 }, position: 'relative' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Stack
            direction={{ xs: 'row', lg: 'column' }}
            alignItems={{ xs: 'center', sm: 'center', md: 'flex-start', lg: 'flex-start' }}
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Box sx={{ flex: 1, pr: { sm: 6, lg: 10 } }}>
              <Typography variant="body1" color="action.active" sx={{ wordWrap: 'break-word', mr: { xs: 1, sm: 0 } }}>
                {event.title}
              </Typography>
              <Typography variant="body2" color="action.active">
                {format(parseISO(event.startDate), 'dd.MM.yy')}
                {format(parseISO(event.startDate), 'dd.MM.yy') !== format(parseISO(event.endDate), 'dd.MM.yy') &&
                  ` - ${format(parseISO(event.endDate), 'dd.MM.yy')}`}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            {!isLargeScreen && showMonetizeCatalog && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpansion(event.id);
                }}
                sx={{ cursor: 'pointer' }}
              >
                <FeaturesStatusIcon state={catalogQuotaIconState} expanded={isExpanded} width={24} height={24} />
              </Box>
            )}
            <EventRowActions event={event} />
          </Stack>
        </Stack>

        {!isLargeScreen && showMonetizeCatalog && catalogProducts && (
          <Collapse in={isExpanded} timeout={300} easing="ease-in-out" unmountOnExit>
            <Box sx={{ mt: 2, pb: 1 }}>
              <EventProductsSection
                event={event}
                products={catalogProducts}
                onCatalogAddClick={onCatalogButtonClick}
                draftLineItemCount={draftLineItemCount}
                isLargeScreen={false}
              />
            </Box>
          </Collapse>
        )}
      </TableCell>
      {isLargeScreen && showMonetizeCatalog && (
        <TableCell sx={{ display: 'table-cell', maxWidth: 0, width: '40%' }}>
          <Box
            sx={{
              overflowX: 'auto',
              maxWidth: '100%',
              width: '100%',
            }}
          >
            <EventProductsSection
              event={event}
              products={catalogProducts || []}
              onCatalogAddClick={onCatalogButtonClick}
              draftLineItemCount={draftLineItemCount}
              isLargeScreen={true}
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
}
